import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowPathIcon,
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ShieldCheckIcon,
  ArrowUturnLeftIcon
} from '@heroicons/react/24/outline';
import { BackupService } from '../../utils/backupService';
import toast from 'react-hot-toast';
import CompanySettings from '../../components/settings/CompanySettings';

const SettingsPage = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isAutoBackupEnabled, setIsAutoBackupEnabled] = useState(false);
  const [autoBackupInterval, setAutoBackupInterval] = useState(30);
  const [autoBackupIntervalId, setAutoBackupIntervalId] = useState(null);
  
  const fileInputRef = useRef(null);
  
  // Gérer l'exportation de la sauvegarde
  const handleExportBackup = async () => {
    setIsExporting(true);
    try {
      await BackupService.exportBackup();
    } catch (error) {
      console.error('Erreur lors de l\'exportation:', error);
      toast.error('Erreur lors de l\'exportation de la sauvegarde');
    } finally {
      setIsExporting(false);
    }
  };
  
  // Gérer l'importation de la sauvegarde
  const handleImportBackup = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setIsImporting(true);
    try {
      await BackupService.importBackup(file);
      // Recharger la page pour appliquer les changements
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Erreur lors de l\'importation:', error);
      toast.error('Erreur lors de l\'importation de la sauvegarde');
    } finally {
      setIsImporting(false);
      // Réinitialiser le champ de fichier
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  // Gérer l'effacement des données
  const handleClearData = async () => {
    setIsClearing(true);
    try {
      const result = await BackupService.clearAllData();
      if (result) {
        // Recharger la page pour appliquer les changements
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      console.error('Erreur lors de l\'effacement des données:', error);
      toast.error('Erreur lors de l\'effacement des données');
    } finally {
      setIsClearing(false);
    }
  };
  
  // Gérer la récupération après effacement
  const handleRecoverAfterClear = async () => {
    try {
      const result = await BackupService.recoverAfterClear();
      if (result) {
        // Recharger la page pour appliquer les changements
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des données:', error);
      toast.error('Erreur lors de la récupération des données');
    }
  };
  
  // Gérer la sauvegarde automatique
  const handleToggleAutoBackup = () => {
    if (isAutoBackupEnabled) {
      // Arrêter la sauvegarde automatique
      BackupService.stopAutoBackup(autoBackupIntervalId);
      setAutoBackupIntervalId(null);
      setIsAutoBackupEnabled(false);
      toast.success('Sauvegarde automatique désactivée');
    } else {
      // Démarrer la sauvegarde automatique
      const intervalId = BackupService.startAutoBackup(autoBackupInterval);
      setAutoBackupIntervalId(intervalId);
      setIsAutoBackupEnabled(true);
      toast.success(`Sauvegarde automatique activée (toutes les ${autoBackupInterval} minutes)`);
    }
  };
  
  // Gérer la restauration de la sauvegarde automatique
  const handleRestoreAutoBackup = async () => {
    try {
      const result = await BackupService.restoreAutoBackup();
      if (result) {
        // Recharger la page pour appliquer les changements
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      console.error('Erreur lors de la restauration de la sauvegarde automatique:', error);
      toast.error('Erreur lors de la restauration de la sauvegarde automatique');
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
        <div>
        <h1 className="text-3xl font-bold text-white">Paramètres</h1>
        <p className="text-white/60 mt-1">Gérez les paramètres de l'application et les sauvegardes de données</p>
        </div>

      {/* Company Settings */}
      <CompanySettings />

      {/* Section des sauvegardes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <h2 className="text-xl font-semibold text-white mb-6">Sauvegarde et Restauration</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Exportation de sauvegarde */}
          <div className="bg-white/5 p-5 rounded-xl border border-white/10 hover:border-white/20 transition-all">
            <div className="flex items-center space-x-4 mb-4">
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <DocumentArrowDownIcon className="h-6 w-6 text-blue-400" />
              </div>
              <h3 className="text-lg font-medium text-white">Exporter les données</h3>
            </div>
            <p className="text-white/70 mb-4">
              Téléchargez une sauvegarde complète de toutes vos données pour les conserver en lieu sûr.
            </p>
            <button
              onClick={handleExportBackup}
              disabled={isExporting}
              className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center"
            >
              {isExporting ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                  Exportation...
                </>
              ) : (
                <>
                  <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                  Exporter
                </>
              )}
            </button>
          </div>
          
          {/* Importation de sauvegarde */}
          <div className="bg-white/5 p-5 rounded-xl border border-white/10 hover:border-white/20 transition-all">
            <div className="flex items-center space-x-4 mb-4">
              <div className="p-3 bg-green-500/20 rounded-lg">
                <DocumentArrowUpIcon className="h-6 w-6 text-green-400" />
              </div>
              <h3 className="text-lg font-medium text-white">Importer des données</h3>
            </div>
            <p className="text-white/70 mb-4">
              Restaurez vos données à partir d'un fichier de sauvegarde précédemment exporté.
            </p>
            <label
              className={`w-full py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center justify-center cursor-pointer ${isImporting ? 'opacity-75 cursor-not-allowed' : ''}`}
            >
              {isImporting ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                  Importation...
                </>
              ) : (
                <>
                  <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
                  Importer
                </>
              )}
              <input
                type="file"
                ref={fileInputRef}
                accept=".json"
                onChange={handleImportBackup}
                disabled={isImporting}
                className="hidden"
              />
            </label>
          </div>
          
          {/* Sauvegarde automatique */}
          <div className="bg-white/5 p-5 rounded-xl border border-white/10 hover:border-white/20 transition-all">
            <div className="flex items-center space-x-4 mb-4">
              <div className="p-3 bg-purple-500/20 rounded-lg">
                <ClockIcon className="h-6 w-6 text-purple-400" />
              </div>
              <h3 className="text-lg font-medium text-white">Sauvegarde automatique</h3>
            </div>
            <p className="text-white/70 mb-4">
              Configurez des sauvegardes automatiques périodiques pour protéger vos données.
            </p>
            <div className="flex items-center space-x-3 mb-4">
              <input
                type="number"
                min="5"
                max="120"
                value={autoBackupInterval}
                onChange={(e) => setAutoBackupInterval(parseInt(e.target.value) || 30)}
                disabled={isAutoBackupEnabled}
                className="w-20 p-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <span className="text-white/70">minutes</span>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleToggleAutoBackup}
                className={`flex-1 py-2 ${
                  isAutoBackupEnabled 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-purple-600 hover:bg-purple-700'
                } text-white rounded-lg transition-colors flex items-center justify-center`}
              >
                {isAutoBackupEnabled ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 mr-2" />
                    Désactiver
                  </>
                ) : (
                  <>
                    <ClockIcon className="h-5 w-5 mr-2" />
                    Activer
                  </>
                )}
              </button>
          <button
                onClick={handleRestoreAutoBackup}
                className="py-2 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
          >
                <ArrowUturnLeftIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

          {/* Réinitialisation des données */}
          <div className="bg-white/5 p-5 rounded-xl border border-white/10 hover:border-white/20 transition-all">
            <div className="flex items-center space-x-4 mb-4">
              <div className="p-3 bg-red-500/20 rounded-lg">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-400" />
              </div>
              <h3 className="text-lg font-medium text-white">Réinitialisation</h3>
            </div>
            <p className="text-white/70 mb-4">
              Effacez toutes les données et réinitialisez l'application. Cette action est irréversible.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={handleClearData}
                disabled={isClearing}
                className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center justify-center"
              >
                {isClearing ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                    Effacement...
                  </>
                ) : (
                  <>
                    <TrashIcon className="h-5 w-5 mr-2" />
                    Tout effacer
                  </>
                )}
              </button>
              <button
                onClick={handleRecoverAfterClear}
                className="py-2 px-4 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
              >
                <ArrowUturnLeftIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Section de sécurité */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <h2 className="text-xl font-semibold text-white mb-6">Sécurité des données</h2>
        
        <div className="bg-white/5 p-5 rounded-xl border border-white/10">
          <div className="flex items-center space-x-4 mb-4">
            <div className="p-3 bg-emerald-500/20 rounded-lg">
              <ShieldCheckIcon className="h-6 w-6 text-emerald-400" />
            </div>
            <h3 className="text-lg font-medium text-white">Stockage local</h3>
          </div>
          <p className="text-white/70 mb-4">
            Toutes vos données sont actuellement stockées localement dans votre navigateur.
            Il est fortement recommandé d'exporter régulièrement vos données pour éviter toute perte.
          </p>
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-amber-300">
            <p className="text-sm">
              <strong>Attention:</strong> La suppression des données de navigation ou l'utilisation du mode navigation privée peut entraîner la perte de vos données.
              Exportez régulièrement vos données pour les sécuriser.
            </p>
          </div>
        </div>
        </motion.div>
    </div>
  );
};

export default SettingsPage; 